import { safeLocalStorage, safeSessionStorage } from 'wsm-tagging-utils';
import findDealerCode from './findDealerCode';
import handleLookups from './handleLookups';
import getDotNotationFromString from './getDotNotationFromString';
import getEnv from './getEnv';
import slugMap from './slugMap';
import { getCookie } from './getCookie';

/**
 *
 * Check a number of places for the replacement value:
 * 1) slugMap
 * 2) window.DDC.WidgetData['ws-tagging'].mapping[mappingConfigurationName]
 * 3) eventData
 * 4) additional slugs
 *
 * The vehicle property must exist (DDC.dataLayer.vehicle) or there will be a js error.
 *
 * @param {String} str The string to replace the slugs in.
 * @param {Object} [eventData] The eventData from the current event.
 * @param {Object} mapping The mapping object (pagealias, form, etc.)
 * @param {Object} utils The current project/grouping utils.
 * @param {Object} lookups The lookups object for the current configuration
 * @param {Object} flags A feature flags object
 * @returns {String} The str with the slugs replaced.
 */
const getReplacement = ({
	str,
	eventData = {},
	mapping,
	utils,
	lookups,
	flags = {},
	payload
}) => {
	let workItem = str;
	let replacement;

	if (str.includes('lookup.')) {
		workItem = workItem.replace('lookup.', '');
	}

	// special handling for single vehicle lookups
	if (slugMap[workItem] && slugMap[workItem].startsWith('vehicle.')) {
		const vehicle =
			// single vehicle lookups should only happen on pages with 1 vehicle
			(window.DDC.dataLayer?.vehicles?.length === 1 &&
				window.DDC.dataLayer.vehicles[0]) ||
			// form confirmation pages
			window.DDC.dataLayer?.confirmation?.vehicle ||
			// allows [[vin]] to work to get vehicle data from eventData
			eventData?.vehicle;

		replacement =
			vehicle && Object.keys(vehicle).length
				? vehicle[slugMap[workItem].replace('vehicle.', '')]
				: '';
	} else if (workItem === 'eventData') {
		replacement = eventData;
	} else if (workItem === 'env') {
		// special handling for env lookups
		replacement = getEnv();
	} else if (workItem.startsWith('dealerCode.')) {
		const dealerCodes = window?.DDC?.dataLayer?.dealership?.dealerCode;
		if (dealerCodes) {
			const targetId = workItem.replace('dealerCode.', '');
			replacement = findDealerCode(dealerCodes, targetId);
		}
	} else if (workItem.startsWith('fn.')) {
		const vehicles = window.DDC.dataLayer?.confirmation?.vehicle
			? [window.DDC.dataLayer.confirmation.vehicle]
			: window.DDC.dataLayer.vehicles;

		workItem = workItem.replace('fn.', '');
		const argsStr = workItem.substring(
			workItem.indexOf('(') + 1,
			workItem.lastIndexOf(')')
		);
		workItem = workItem.replace(`(${argsStr})`, '');

		if (utils[workItem]) {
			replacement = utils[workItem]({
				eventData,
				mapping,
				payload,
				vehicles,
				window
			});
		} else {
			replacement = undefined;

			// eslint-disable-next-line no-console
			console.error(
				`The ${workItem} function in utils was not exported or does not exist.`
			);
		}
	} else if (workItem.startsWith('flags.')) {
		workItem = workItem.replace('flags.', '');

		replacement = flags[workItem];
	} else if (workItem === '_dl' || workItem === 'window._dl') {
		// TODO: How can we now have this here? This is client specific.
		replacement = window._dl;
	} else if (workItem.startsWith('cookie.')) {
		workItem = workItem.replace('cookie.', '');
		const cookie = getCookie(workItem, undefined);
		// getCookie returns null when not found but undefined is standard for
		// getReplacement when something not found
		replacement = cookie === null ? undefined : cookie;
	} else if (workItem === 'userSessionId') {
		replacement = safeSessionStorage.getItem('userSessionId');
	} else {
		// The below section is to find values from dot notation.
		let scope;
		if (slugMap[workItem]) {
			scope = window;
			replacement = `DDC.${slugMap[workItem]}`;
		} else if (workItem.startsWith('mapping.')) {
			scope = mapping;
			replacement = workItem.replace('mapping.', '');
		} else if (workItem.startsWith('eventData.')) {
			scope = eventData;
			replacement = workItem.replace('eventData.', '');
		} else if (workItem.startsWith('payload.')) {
			// for internal use allowing clientEventName to be a slug
			scope = payload;
			replacement = workItem.replace('payload.', '');
		} else if (workItem.startsWith('DDC.')) {
			scope = window;
			replacement = workItem;
		} else if (workItem.startsWith('document.')) {
			scope = document;
			replacement = workItem.replace('document.', '');
		} else if (workItem.startsWith('localstorage.')) {
			workItem = workItem.replace('localstorage.', '');
			const localStorageKey = workItem.split('.')[0];
			scope =
				JSON.parse(safeLocalStorage.getItem(localStorageKey)) ||
				undefined;
			replacement = workItem.replace(`${localStorageKey}.`, '');
		} else if (workItem.startsWith('previousEvent.')) {
			workItem = workItem.replace('previousEvent.', '');
			const event = workItem.split('.')[0];
			scope = mapping?.prevEventData?.[event];
			replacement = workItem.replace(`${event}.`, '');
		} else if (workItem.startsWith('window.')) {
			workItem = workItem.replace('window.', '');
			scope = window;
			replacement = workItem;
		}

		replacement =
			scope &&
			replacement &&
			getDotNotationFromString(scope, replacement);
	}

	return str.includes('lookup.')
		? handleLookups(workItem, replacement, lookups)
		: replacement;
};

export default getReplacement;
